import { Button, Color, IconName, Spacing, Text } from 'zens-storybook'
import React, { PropsWithChildren, ReactElement } from 'react'
import { StyleProp, StyleSheet, View, ViewProps, ViewStyle } from 'react-native'
import { useTranslation } from 'react-i18next'

import { Href, Link } from 'zens-router'

import { UserActivity } from '../user'

import InfoBlock from './block'

const styles = StyleSheet.create({
  container: {},
  content: {},
  fields: {
    flexDirection: 'row',
  },
  labels: {
    marginRight: Spacing.XXL,
    flex: 1,
  },
  values: {
    flex: 3,
  },
  row: {
    height: 32,
    justifyContent: 'center',
  },
  activities: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: Color.calmGrey,
    marginTop: Spacing.S,
    paddingTop: Spacing.M,
  },
  group: {
    minWidth: 500,
    marginVertical: Spacing.S,
  },
  groupTitle: {
    color: Color.metalGrey,
    marginBottom: Spacing.XXS,
  },
  columns: {
    flexDirection: 'row',
  },
  column: {
    flex: 1,
  },
  skeletonActivity: {
    flex: 1,
  },
})

interface Field {
  label: string
  value?: string | number | ReactElement
}

interface Activity {
  createdBy?: number
  createdAt?: string
  updatedBy?: number
  updatedAt?: string
}

type FieldConfig = {
  fields: Field[]
}

interface Group extends FieldConfig {
  label: string
}

type GroupConfig = {
  groups: Group[]
}

export type Config = FieldConfig | GroupConfig

type ColumnConfig = {
  columns: Config[]
}

export interface Props extends ViewProps {
  companyId: number
  title?: string
  config?: Config | ColumnConfig
  activity?: Activity
  unfolded?: boolean
  contentContainerStyle?: StyleProp<ViewStyle>
  buttonTitle?: string
  buttonIcon?: IconName
  buttonHref?: Href
  buttonPress?: () => void
}
const InfoTable = (props: PropsWithChildren<Props>) => {
  const {
    companyId,
    title,
    style,
    config,
    activity,
    collapsable,
    contentContainerStyle,
    buttonTitle,
    buttonIcon,
    buttonHref,
    buttonPress,
    children,
    ...otherProps
  } = props

  const { t } = useTranslation()

  const renderFields = (config?: FieldConfig) => {
    if (!config?.fields) return null
    return (
      <View style={[styles.fields, contentContainerStyle]}>
        <View style={styles.labels}>
          {config.fields.map((field, index) => (
            <View key={`label-${index}`} style={styles.row}>
              <Text level={5} numberOfLines={1}>
                {field.label}
              </Text>
            </View>
          ))}
        </View>
        <View style={styles.values}>
          {config.fields.map((field, index) => (
            <View key={`value-${index}`} style={styles.row}>
              {typeof field.value === 'string' || typeof field.value === 'number' ? (
                <Text numberOfLines={1}>{field.value}</Text>
              ) : (
                field.value
              )}
            </View>
          ))}
        </View>
      </View>
    )
  }

  const renderGroups = (config?: GroupConfig) => {
    if (!config?.groups) return null

    return (
      <View>
        {config.groups.map((group, index) => (
          <View key={`group-${index}`} style={styles.group}>
            <Text level={5} numberOfLines={1} style={styles.groupTitle}>
              {group.label}
            </Text>
            {renderFields(group)}
          </View>
        ))}
      </View>
    )
  }

  const renderActivity = () => {
    if (!activity) return null
    return (
      <View style={styles.activities}>
        {activity.createdAt && (
          <UserActivity companyId={companyId} activity="added" date={activity.createdAt} userId={activity.createdBy} />
        )}
        {activity.updatedAt && (
          <UserActivity
            companyId={companyId}
            activity="updated"
            date={activity.updatedAt}
            userId={activity.updatedBy}
          />
        )}
      </View>
    )
  }

  const renderItems = (config?: Config) =>
    config?.hasOwnProperty('groups') ? (
      renderGroups(config as GroupConfig)
    ) : (
      <View style={styles.content}>{renderFields(config as FieldConfig)}</View>
    )

  const renderColumns = (config?: ColumnConfig) => (
    <View style={styles.columns}>
      {config?.columns.map((column, index) => (
        <View key={`col-${index}`} style={styles.column}>
          {renderItems(column)}
        </View>
      ))}
    </View>
  )

  const button = (
    <Button
      title={buttonTitle || t('Edit details')}
      light
      left={buttonIcon || 'pencil'}
      testID="InfoTable.Edit"
      onPress={buttonPress}
    />
  )
  const right = buttonHref ? (
    <Link href={buttonHref} asChild>
      {button}
    </Link>
  ) : buttonPress ? (
    button
  ) : undefined

  return (
    <InfoBlock
      title={
        title
          ? {
              text: title,
              right,
            }
          : undefined
      }
      right={!title ? right : undefined}
      style={[styles.container, style]}
      collapsable={collapsable}
      {...otherProps}
    >
      {config?.hasOwnProperty('columns') ? renderColumns(config as ColumnConfig) : renderItems(config as Config)}
      {renderActivity()}
      {children}
    </InfoBlock>
  )
}

interface SkeletonProps extends ViewProps {
  title?: string | ReactElement | true
  hasActivity?: boolean
}

InfoTable.Skeleton = (props: SkeletonProps) => {
  const { title, hasActivity, style, ...otherProps } = props
  return (
    <InfoBlock.Skeleton title={title} style={[styles.container, style]} {...otherProps}>
      <Text.Skeleton style={styles.row} />
      <Text.Skeleton style={styles.row} />
      <Text.Skeleton style={styles.row} />
      {hasActivity && (
        <View style={styles.activities}>
          <Text.Skeleton style={styles.skeletonActivity} />
        </View>
      )}
    </InfoBlock.Skeleton>
  )
}

export default InfoTable
